import { useCallback, useEffect, useMemo } from 'react';
import { InView } from 'react-intersection-observer';
import { debounce } from 'lodash';
import { PostCardSkeleton } from 'src/components/postCardSkeleton';
import { useUserProfile } from 'src/context/userProfileContext';
import { useTrackEvents } from 'src/context/userProfileContext/hooks';
import { ComponentSource, ContentType, TryResult } from 'src/utils/datahubDefaultEvent';
import { Post } from '../post';
import { useGetUserPosts } from '../../hooks';
import { PostsErrorState } from '../postsErrorState';
import { PostsEmptyState } from '../postsEmptyState';

const Posts = () => {
  const { visible, userUcode, loggedUserUcode } = useUserProfile();
  const { sendReadActivitiesEvent } = useTrackEvents({
    loggedUserUcode
  });

  const {
    isLoading,
    isSuccess,
    isError,
    isFetchingNextPage,
    data,
    error,
    hasNextPage,
    fetchNextPage
  } = useGetUserPosts({
    ucode: userUcode,
    enabled: Boolean(userUcode && visible)
  });

  const handleFetchNextPagePostsDebounce = useCallback(
    /* istanbul ignore file */
    debounce((inView: boolean) => {
      if (inView) fetchNextPage();
    }, 500),
    []
  );

  const posts = useMemo(() => {
    return data?.pages.flatMap((page) => page.data);
  }, [data]);

  const isValidPosts = Array.isArray(posts) && posts.length > 0;
  const showInViewComponent = hasNextPage && (!isFetchingNextPage || !isLoading);

  useEffect(() => {
    if (isSuccess && isValidPosts) {
      const itemsCount = posts.length > 10 ? 10 : posts.length;

      sendReadActivitiesEvent({
        contentType: ContentType.POST,
        itemsCount,
        componentSource: ComponentSource.POST_LIST,
        result: TryResult.SUCCESSFUL,
        statusCode: 200
      });
    }

    if (isError) {
      sendReadActivitiesEvent({
        contentType: ContentType.POST,
        itemsCount: 0,
        componentSource: ComponentSource.POST_LIST,
        result: TryResult.FAILURE,
        statusCode: error?.statusCode || 500
      });
    }
  }, [isSuccess, isError]);

  if (isLoading) {
    return <PostCardSkeleton length={2} />;
  }

  if (isError && !isValidPosts) {
    return <PostsErrorState />;
  }

  if (!isValidPosts) {
    return <PostsEmptyState />;
  }

  return (
    <div className="_px-3 _pb-5">
      {posts?.map((post) => (
        <Post key={post.id} post={post} />
      ))}

      {showInViewComponent && (
        <InView as="div" onChange={handleFetchNextPagePostsDebounce}>
          <PostCardSkeleton />
        </InView>
      )}
    </div>
  );
};

export default Posts;
